import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';

function Gallery() {
  const [content, setContent] = useState({ 
    photos: [], 
    youtubeVideos: [], 
    instagramEmbed: '', 
    title: 'Photo Gallery & Videos',
    sliderImages: [] 
  });
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('photos');

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPageContent('gallery');
        setContent(prev => ({ ...prev, ...data }));
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('gallery', (data) => { 
      if (data) setContent(prev => ({ ...prev, ...data })); 
    });
    return () => unsubscribe();
  }, []);

  const getYouTubeId = (url) => { 
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?#]+)/); 
    return match ? match[1] : null; 
  };

  const photos = content.photos || [];
  const videos = content.youtubeVideos || [];
  const instagramEmbed = content.instagramEmbed || '';

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Gallery...</p>
      </div>
    );
  }

  return (
    <div className="gallery-page">
        <div className="container">
          <h1>{content.title}</h1>
          <div className="hero-divider"></div>
          <p>Memorable Moments • Events • Achievements</p>
        </div>
     

      <div className="container">
        {/* Tabs for Photos and Videos */}
        {(photos.length > 0 || videos.length > 0) && (
          <div className="gallery-tabs">
            {photos.length > 0 && (
              <button 
                className={`tab-btn ${activeTab === 'photos' ? 'active' : ''}`}
                onClick={() => setActiveTab('photos')}
              >
                📷 Photos ({photos.length})
              </button>
            )}
            {videos.length > 0 && (
              <button 
                className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
                onClick={() => setActiveTab('videos')}
              >
                🎬 YouTube Videos ({videos.length})
              </button>
            )}
            {instagramEmbed && (
              <button 
                className={`tab-btn ${activeTab === 'instagram' ? 'active' : ''}`}
                onClick={() => setActiveTab('instagram')}
              >
                📸 Instagram Feed
              </button>
            )}
          </div>
        )}

        {/* Photos Grid */}
        {activeTab === 'photos' && photos.length > 0 && (
          <div className="photos-section">
            <div className="photos-grid">
              {photos.map((photo, idx) => (
                <div key={idx} className="photo-card" onClick={() => setSelectedImage(photo.url)}>
                  <div className="photo-image">
                    <img src={photo.url} alt={photo.title || 'Gallery Image'} />
                    <div className="photo-overlay">
                      {photo.title && <h4>{photo.title}</h4>}
                      {photo.caption && <p>{photo.caption}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YouTube Videos Grid */}
        {activeTab === 'videos' && videos.length > 0 && (
          <div className="videos-section">
            <div className="videos-grid">
              {videos.map((video, idx) => {
                const videoId = getYouTubeId(video.url);
                return videoId ? (
                  <div key={idx} className="video-card">
                    <div className="video-wrapper">
                      <iframe 
                        src={`https://www.youtube.com/embed/${videoId}`} 
                        title={video.title || 'YouTube Video'} 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                      ></iframe>
                    </div>
                    {video.title && <h4>{video.title}</h4>}
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Instagram Feed */}
        {activeTab === 'instagram' && instagramEmbed && (
          <div className="instagram-section">
            <div className="instagram-container">
              <iframe 
                src={instagramEmbed} 
                frameBorder="0" 
                scrolling="no" 
                allowTransparency={true}
                title="Instagram Feed"
              ></iframe>
            </div>
          </div>
        )}

        {/* Empty State */}
        {photos.length === 0 && videos.length === 0 && !instagramEmbed && (
          <div className="empty-state">
            <div className="empty-icon">🖼️</div>
            <h3>No Gallery Content Yet</h3>
            <p>Photos and videos will be added soon. Please check back later.</p>
          </div>
        )}

      </div>

      {/* Lightbox for fullscreen images */}
      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)}>
          <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
          <img src={selectedImage} alt="Full size" />
        </div>
      )}

      <style>{`
        .gallery-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #1a3a6b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        /* Page Hero */
        .page-hero {
          background: linear-gradient(135deg, #1a3a6b, #f59e0b);
          color: white;
          text-align: center;
          padding: 60px 20px;
          margin-bottom: 40px;
        }

        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
          font-weight: 700;
        }

        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }

        /* Gallery Tabs */
        .gallery-tabs {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }

        .tab-btn {
          padding: 12px 28px;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 50px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          color: #475569;
        }

        .tab-btn:hover {
          border-color: #1a3a6b;
          color: #1a3a6b;
        }

        .tab-btn.active {
          background: #1a3a6b;
          border-color: #1a3a6b;
          color: white;
        }

        /* Photos Grid */
        .photos-section {
          margin-bottom: 40px;
        }

        .photos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 25px;
        }

        .photo-card {
          cursor: pointer;
          border-radius: 16px;
          overflow: hidden;
          background: white;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .photo-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
        }

        .photo-image {
          position: relative;
          height: 250px;
          overflow: hidden;
        }

        .photo-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s;
        }

        .photo-card:hover .photo-image img {
          transform: scale(1.05);
        }

        .photo-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          padding: 20px;
          color: white;
          transform: translateY(100%);
          transition: transform 0.3s;
        }

        .photo-card:hover .photo-overlay {
          transform: translateY(0);
        }

        .photo-overlay h4 {
          font-size: 16px;
          margin-bottom: 5px;
        }

        .photo-overlay p {
          font-size: 12px;
          opacity: 0.9;
        }

        /* Videos Grid */
        .videos-section {
          margin-bottom: 40px;
        }

        .videos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 30px;
        }

        .video-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
        }

        .video-card:hover {
          transform: translateY(-5px);
        }

        .video-wrapper {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
        }

        .video-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        .video-card h4 {
          padding: 15px;
          margin: 0;
          color: #1a3a6b;
          font-size: 16px;
          font-weight: 600;
        }

        /* Instagram Section */
        .instagram-section {
          margin-bottom: 40px;
        }

        .instagram-container {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          padding: 20px;
        }

        .instagram-container iframe {
          width: 100%;
          min-height: 500px;
          border: none;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 80px 20px;
          background: white;
          border-radius: 20px;
          margin: 40px 0;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #1a3a6b;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #64748b;
        }

        /* Back Link */
        .back-link {
          text-align: center;
          margin-top: 40px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e2e8f0;
          color: #475569;
          padding: 12px 28px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }

        .back-btn:hover {
          background: #1a3a6b;
          color: white;
          transform: translateX(-5px);
        }

        /* Lightbox */
        .lightbox {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          cursor: pointer;
        }

        .lightbox img {
          max-width: 90%;
          max-height: 90%;
          object-fit: contain;
        }

        .lightbox-close {
          position: absolute;
          top: 20px;
          right: 30px;
          font-size: 48px;
          color: white;
          background: none;
          border: none;
          cursor: pointer;
          transition: transform 0.3s;
        }

        .lightbox-close:hover {
          transform: scale(1.1);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 0 20px 50px;
          }
        }

        @media (max-width: 768px) {
          .page-hero {
            padding: 50px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .page-hero p {
            font-size: 16px;
          }

          .gallery-tabs {
            gap: 10px;
          }

          .tab-btn {
            padding: 8px 20px;
            font-size: 14px;
          }

          .photos-grid {
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 20px;
          }

          .photo-image {
            height: 200px;
          }

          .videos-grid {
            grid-template-columns: 1fr;
          }

          .instagram-container iframe {
            min-height: 400px;
          }
        }

        @media (max-width: 480px) {
          .page-hero h1 {
            font-size: 28px;
          }

          .photos-grid {
            grid-template-columns: 1fr;
          }

          .tab-btn {
            padding: 6px 16px;
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
}

export default Gallery;